"use client"

import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  CircularProgress,
  Alert,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  InputLabel,
} from "@mui/material"
import { ArrowBack, Phone, LocationOn, Person, Badge, LocalHospital, Close, Visibility, ExpandMore } from "@mui/icons-material"
import { fetchProviderServicesAdaptive, fetchProviderLocationsAdaptive, fetchProviderNegotiatedRateAdaptive, fetchDeductibleValue as fetchDeductibleValueAPI } from "./providers-api"

const ProviderDetails = ({ provider, searchCriteria, onBack, isPartnerRoute = false, isPublicRoute = false }) => {
  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(true)
  const [servicesPage, setServicesPage] = useState(0)
  const [hasMoreServices, setHasMoreServices] = useState(false)
  const [servicesError, setServicesError] = useState(null)
  
  // New state for locations
  const [locations, setLocations] = useState([])
  const [locationsLoading, setLocationsLoading] = useState(true)
  const [locationsError, setLocationsError] = useState(null)
  
  // Drawer state for service details
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedService, setSelectedService] = useState(null)
  
  // Accordion and modifier states
  const [billingClass, setBillingClass] = useState("professional")
  const [selectedModifier, setSelectedModifier] = useState("Other")
  const [modifierData, setModifierData] = useState([])
  const [modifierLoading, setModifierLoading] = useState(false)
  const [modifierCache, setModifierCache] = useState({})
  const [deductibleValue, setDeductibleValue] = useState(null)

  useEffect(() => {
    loadProviderServices(0, true)
    loadProviderLocations()
  }, [provider?.id, searchCriteria?.plan?.id])

  const loadProviderServices = async (page = 0, replace = false) => {
    if (!provider?.id || !searchCriteria?.plan?.id) {
      setServicesLoading(false)
      return
    }

    setServicesLoading(true)
    setServicesError(null)

    try {
      const response = await fetchProviderServicesAdaptive(
        provider.id,
        searchCriteria.plan.id,
        searchCriteria.organizationName || "",
        page,
        10,
        "",
        isPublicRoute
      )

      const newServices = response.services || []
      
      if (replace) {
        setServices(newServices)
      } else {
        setServices((prev) => [...prev, ...newServices])
      }

      // Check if there are more services to load
      setHasMoreServices(newServices.length === 10)
      setServicesPage(page)
    } catch (error) {
      console.error("Error loading provider services:", error)
      setServicesError("Failed to load services. Please try again.")
      if (replace) {
        setServices([])
      }
    } finally {
      setServicesLoading(false)
    }
  }

  const loadProviderLocations = async () => {
    if (!provider?.id) {
      setLocationsLoading(false)
      return
    }

    setLocationsLoading(true)
    setLocationsError(null)

    try {
      const response = await fetchProviderLocationsAdaptive(
        provider.id,
        searchCriteria.organizationName || "",
        0,
        20,
        "",
        "",
        isPublicRoute
      )
      
      const providerLocations = (response.content || []).map((loc) => ({
        id: loc.id,
        name: loc.name || `Location`,
        address: `${loc.address}, ${loc.city}, ${loc.state} ${loc.postalCode}`,
        phone: loc.telephoneNumber,
        coordinates: {
          lat: parseFloat(loc.latitude),
          lng: parseFloat(loc.longitude),
        },
      }))

      setLocations(providerLocations)
    } catch (error) {
      console.error("Error loading provider locations:", error)
      setLocationsError("Failed to load locations. Please try again.")
      setLocations([])
    } finally {
      setLocationsLoading(false)
    }
  }

  const handleLoadMoreServices = () => {
    const nextPage = servicesPage + 1
    loadProviderServices(nextPage, false)
  }

  const handleRetryLocations = () => {
    loadProviderLocations()
  }
  
  const handleViewDetails = async (service) => {
    setSelectedService(service)
    setDrawerOpen(true)

    // Always reopen with the default billing class/modifier and a clean cache for this session
    setBillingClass("professional")
    setSelectedModifier("Other")
    setModifierData([])
    setModifierCache({})

    // Fetch deductible value
    await fetchDeductibleValue()

    // Fetch modifier data for the default "professional" billing class, using a fresh cache
    await fetchModifierData(service, "professional", {})
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedService(null)
    setModifierData([])
    setDeductibleValue(null)
    setBillingClass("professional")
    setSelectedModifier("Other")
    setModifierCache({})
  }
  
  const fetchDeductibleValue = async () => {
    const subscriberId = localStorage.getItem('findCareSubscriberId')
    if (!subscriberId) return
    
    try {
      const response = await fetchDeductibleValueAPI([subscriberId])
      
      const deductibleData = response?.[0]
      if (deductibleData) {
        setDeductibleValue(parseFloat(deductibleData.deductibleValue) || 0)
      }
    } catch (error) {
      console.error("Error fetching deductible value:", error)
    }
  }
  
  const fetchModifierData = async (service, billingClassParam, cacheOverride) => {
    const activeBillingClass = billingClassParam ?? billingClass
    const activeCache = cacheOverride ?? modifierCache
    const planUid = localStorage.getItem('findCarePlanUid') || searchCriteria?.plan?.id
    const procedureCode = service.procedureCode || service.billingCode

    if (!provider?.npi && !provider?.id || !planUid || !procedureCode) return

    const cacheKey = `${procedureCode}_${activeBillingClass}`
    if (activeCache[cacheKey]) {
      setModifierData(activeCache[cacheKey])
      return
    }

    setModifierLoading(true)
    try {
      const response = await fetchProviderNegotiatedRateAdaptive({
        npiNumber: provider.npi || provider.id,
        planUid: planUid,
        billingCode: procedureCode,
        billingClass: activeBillingClass,
        organizationName: searchCriteria.organizationName || "",
        page: 0,
        size: 50
      }, isPublicRoute)

      const result = response || []
      setModifierData(result)
      setModifierCache((prev) => ({ ...prev, [cacheKey]: result }))
    } catch (error) {
      console.error("Error fetching modifier data:", error)
      setModifierData([])
    } finally {
      setModifierLoading(false)
    }
  }

  const handleBillingClassChange = async (event) => {
    const newBillingClass = event.target.value
    setBillingClass(newBillingClass)

    if (selectedModifier === "Other" && selectedService) {
      await fetchModifierData(selectedService, newBillingClass)
    }
  }

  const handleModifierChange = async (event) => {
    setSelectedModifier(event.target.value)
    if (event.target.value === "Other" && selectedService) {
      await fetchModifierData(selectedService, billingClass)
    }
  }
  
  const calculateOOPRate = (negotiatedRate) => {
    const deductibleVal = deductibleValue || 0
    const rate = parseFloat(negotiatedRate) || 0
    
    let result = 0
    if (deductibleVal > rate) {
      result = rate
    } else if (deductibleVal < rate) {
      result = deductibleVal
    } else {
      result = rate
    }
    
    return `$${result.toFixed(2)}`
  }

  if (!provider) {
    return (
      <Box sx={{ p: 3,pl: 0, textAlign: "center" }}>
        <Typography>Provider not found</Typography>
        <Button onClick={onBack} sx={{ mt: 2 }}>
          Back to results
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5" }}>
      <Box sx={{ mx: "auto", p: 2, pl: 0 }}>
        <Grid container spacing={3}>
          {/* Provider Information */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Provider information
              </Typography>

              <Grid container spacing={3}>
                {/* Basic Info */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Person sx={{ color: "#666" }} />
                    <Box>
                      <Typography color="text.secondary">
                        Gender
                      </Typography>
                      <Typography variant="body1">{provider.gender === 'F' ? "Female" : provider.gender === 'M' ? "Male" : "Not specified"}</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                    <Badge sx={{ color: "#666" }} />
                    <Box>
                      <Typography color="text.secondary">
                        NPI
                      </Typography>
                      <Typography variant="body1">{provider.npi || provider.id || "Not available"}</Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Specialties */}
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
                    <LocalHospital sx={{ color: "#666", mt: 0.5 }} />
                    <Box>
                      <Typography color="text.secondary" sx={{ mb: 1 }}>
                        Specialties
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {provider.specialties && provider.specialties.length > 0 ? (
                          provider.specialties.map((specialty, index) => (
                            <Chip
                              key={index}
                              label={typeof specialty === 'string' ? specialty : specialty.name}
                              size="small"
                              variant="outlined"
                              sx={{ borderColor: "#1976d2", color: "#1976d2" }}
                            />
                          ))
                        ) : (
                          <Chip
                            label="Internal Medicine"
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: "#1976d2", color: "#1976d2" }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Services and Rates */}
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Services and rates
              </Typography>

              {servicesLoading && services.length === 0 ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : services.length === 0 && !servicesLoading ? (
                <Typography color="text.secondary" sx={{ textAlign: "center", py: 3 }}>
                  {searchCriteria?.plan?.id ? "No services found for this provider and plan." : "Plan information required to load services."}
                </Typography>
              ) : (
                <TableContainer>
                  <Table sx={{ 
                    '& .MuiTableHead-root': {
                      bgcolor: '#f8f9fa'
                    }
                  }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#1B1B1B', py: 2 }}>Service name</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#1B1B1B', py: 2 }}>Procedure code</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#1B1B1B', py: 2 }}>Negotiated rate</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#1B1B1B', py: 2 }}>Rate type</TableCell>
                        {(isPartnerRoute || isPublicRoute) && (
                          <TableCell sx={{ fontWeight: 600, fontSize: '14px', color: '#1B1B1B', py: 2 }}>Actions</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {services.map((service, index) => (
                        <TableRow 
                          key={index}
                          sx={{
                            '&:hover': { bgcolor: '#f8f9fa' },
                            borderBottom: '1px solid #e0e0e0'
                          }}
                        >
                          <TableCell sx={{ py: 2.5, fontSize: '14px' }}>
                            <Typography sx={{ fontWeight: 500 }}>
                              {service.serviceName || service.name || "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2.5, fontSize: '14px', color: '#666' }}>
                            {service.procedureCode || service.billingCode || "N/A"}
                          </TableCell>
                          <TableCell sx={{ py: 2.5 }}>
                            <Typography  sx={{ fontWeight: 600, color: '#1976d2', fontSize: '15px' }}>
                              {service.negotiatedRate ? `$${service.negotiatedRate}` : "N/A"}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 2.5 }}>
                            <Chip 
                              label={service.negotiatedType || service.billingCodeType || "N/A"} 
                              size="small"
                              sx={{ 
                                bgcolor: '#e3f2fd', 
                                color: '#1976d2',
                                fontWeight: 500,
                                fontSize: '12px'
                              }}
                            />
                          </TableCell>
                          {(isPartnerRoute || isPublicRoute) && (
                            <TableCell sx={{ py: 2.5 }}>
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<Visibility />}
                                onClick={() => handleViewDetails(service)}
                                sx={{ 
                                  textTransform: 'none',
                                  borderRadius: 1.5,
                                  px: 2
                                }}
                              >
                                View details
                              </Button>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>

                  {/* Load More Services Button */}
                  {hasMoreServices && (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={handleLoadMoreServices}
                        disabled={servicesLoading}
                        sx={{ textTransform: "none" }}
                      >
                        {servicesLoading ? (
                          <>
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                            Loading...
                          </>
                        ) : (
                          "Load More Services"
                        )}
                      </Button>
                    </Box>
                  )}
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Locations Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Locations
              </Typography>

              {locationsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {locationsError}
                  <Button 
                    onClick={handleRetryLocations} 
                    size="small" 
                    sx={{ ml: 1 }}
                  >
                    Retry
                  </Button>
                </Alert>
              )}

              {locationsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : locations && locations.length > 0 ? (
                locations.map((location, index) => (
                  <Box
                    key={location.id || index}
                    sx={{
                      mb: 3,
                      pb: 3,
                      borderBottom: index < locations.length - 1 ? "1px solid #e0e0e0" : "none",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
                      <LocationOn sx={{ color: "#666", mt: 0.2 }} />
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                          {location.name || `Location ${index + 1}`}
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                          {location.address}
                        </Typography>
                      </Box>
                    </Box>

                    {location.phone && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                        <Phone sx={{ color: "#666" }} />
                        <Link href={`tel:${location.phone}`} sx={{ color: "#1976d2", textDecoration: "none" }}>
                          {location.phone}
                        </Link>
                      </Box>
                    )}

                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      onClick={() => {
                        const address = encodeURIComponent(location.address)
                        window.open(`https://maps.google.com?q=${address}`, "_blank")
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Get directions
                    </Button>
                  </Box>
                ))
              ) : (
                <Typography color="text.secondary">
                  {locationsLoading ? "Loading locations..." : "No location information available."}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
      
      {/* Service Details Modal */}
    <Dialog
      open={drawerOpen}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return
        handleCloseDrawer()
      }}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 900,
          borderRadius: 2,
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* ================= Header ================= */}
        <Box sx={{ p: 3, borderBottom: "1px solid #e5e7eb", position: "relative" }}>
          <IconButton
            onClick={handleCloseDrawer}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <Close />
          </IconButton>

          <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
            {selectedService?.serviceName}
          </Typography>

          <Typography sx={{ fontSize: 14, color: "#6b7280", mt: 0.5 }}>
            Procedure code: {selectedService?.procedureCode}
          </Typography>
        </Box>

        {/* ================= Provider Card ================= */}
          <Box sx={{ p: 3 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: "#f9fafb",
              }}
            >
              {/* Header Row */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    bgcolor: "#ef3b2d",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: 16,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {provider?.name?.charAt(0)}
                </Box>

                <Box>
                  <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
                    {provider?.name}
                  </Typography>

                  {/* IN NETWORK Pill */}
                  <Box
                    sx={{
                      display: "inline-block",
                      mt: 0.5,
                      px: 1.5,
                      py: 0.25,
                      borderRadius: 999,
                      border: "1px solid #34a853",
                      color: "#137333",
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: "#e6f4ea",
                    }}
                  >
                    IN NETWORK
                  </Box>
                </Box>
              </Box>

              {/* Details */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Specialty */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <LocalHospital sx={{ fontSize: 18, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
                    {provider?.specialties
                      ?.map((s) => (typeof s === "string" ? s : s.name))
                      .join(", ")}
                  </Typography>
                </Box>

                {/* NPI */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Badge sx={{ fontSize: 18, color: "#6b7280" }} />
                  <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
                    {provider?.npi} (NPI Code)
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>

        {/* ================= Negotiated Rate ================= */}
        <Box sx={{ px: 3 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 2,
              bgcolor: "#f9fafb",
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#6b7280" }}>
              Negotiated rate
            </Typography>

            <Typography sx={{ fontSize: 28, fontWeight: 700 }}>
              {selectedService?.negotiatedRate ? `$${selectedService.negotiatedRate}` : "N/A"}
            </Typography>
          </Paper>
        </Box>

        {/* ================= Accordion ================= */}
        <Box sx={{ p: 3 }}>
          <Accordion
            elevation={0}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              "&:before": { display: "none" },
              backgroundColor: "#f9fafb"
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontWeight: 600 }}>
                Negotiated rate based on modifier and place of service
              </Typography>
            </AccordionSummary>

            <AccordionDetails>
              {/* ===== Billing Class ===== */}
              <RadioGroup
                row
                value={billingClass}
                onChange={handleBillingClassChange}
                sx={{ mb: 2 }}
              >
                <FormControlLabel
                  value="professional"
                  control={<Radio />}
                  label="Professional"
                />
                <FormControlLabel
                  value="institutional"
                  control={<Radio />}
                  label="Institutional"
                />
              </RadioGroup>

              {/* ===== Modifier + Pricing (Professional / Institutional) ===== */}
              <>
                {/* Modifier */}
                <Box sx={{ mb: 2 }}>
                  <Typography
                    sx={{ fontSize: 14, color: "#6b7280", mb: 0.5 }}
                  >
                    Select modifier
                  </Typography>

                  <Select
                    fullWidth
                    value={selectedModifier}
                    displayEmpty
                    onChange={handleModifierChange}
                    sx={{
                      height: 44,
                      borderRadius: 1,
                      "& fieldset": {
                        borderColor: "#d1d5db",
                      },
                    }}
                  >
                    <MenuItem style={{marginLeft: 15}} value="">
                      <em>Select the modifier</em>
                    </MenuItem>
                    <MenuItem style={{marginLeft: 15}} value="Other">Others</MenuItem>
                  </Select>
                </Box>

                {/* Pricing Table */}
                {selectedModifier === "Other" && (
                  <>
                    {modifierLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={32} />
                      </Box>
                    ) : modifierData.length > 0 ? (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>Place of service</TableCell>
                            <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>Negotiated rate</TableCell>
                            {!isPublicRoute && (
                              <TableCell sx={{ fontSize: 14, fontWeight: 600 }}>Est. out of pocket rate</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {modifierData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell sx={{ fontSize: 14 }}>{row.serviceName}</TableCell>
                              <TableCell sx={{ fontSize: 14 }}>${row.negotiatedRate}</TableCell>
                              {!isPublicRoute && (
                                <TableCell sx={{ fontSize: 14 }}>
                                  {calculateOOPRate(row.negotiatedRate)}
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography sx={{ textAlign: 'center', color: '#6b7280', py: 3, fontSize: 14 }}>
                        No data available
                      </Typography>
                    )}
                  </>
                )}
              </>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
    </Dialog>

    </Box>
  )
}

export default ProviderDetails